import { Anchor, Group, Stack, Text } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { i18nKeys, tKeysLoose } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useHistory } from 'react-router';
import { map, pipe, sortBy } from 'remeda';
import { SendingMediumSelector } from 'shared/components/SendingMedium/SendingMediumSelector';
import type {
  CompanyConfiguration,
  SendingMedium,
} from 'shared/hooks/use-load-company-configuration';
import type { SendingMedia } from 'types/sending-media';

import type { PreferenceForm } from './usePreferenceForm';

type SendingMediumManagerProps = {
  form: PreferenceForm;
  company: CompanyConfiguration;
};

export const SendingMediumManager = ({ form, company }: SendingMediumManagerProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const createFirstMedium = (name: SendingMedia) => {
    form.setFieldValue('sendingMedium', [{ name, order: 1, force_sending: true }]);
  };

  const formatCompanyDefault = (sendingMedium: Array<SendingMedium>) => {
    if (sendingMedium.length === 0)
      return (
        <Group gap="4" align="center">
          <Text>{t(i18nKeys.SENDING_MEDIUM_MANAGER.NOTHING_SET_BY_DEFAULT)}</Text>
          <Anchor
            onClick={() => history.push('/settings/billing')}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <IconExternalLink size={18} stroke={1.5} />
          </Anchor>
        </Group>
      );

    const toLabel = (medium: SendingMedia) =>
      t(tKeysLoose.REMINDER_LIST.BADGE[medium.toUpperCase()]);

    if (sendingMedium.length === 1) return toLabel(sendingMedium[0].name);

    return pipe(
      sendingMedium,
      sortBy(m => m.order ?? 0),
      map((m, i) =>
        i === 0
          ? toLabel(m.name)
          : `${m.force_sending ? t(i18nKeys.AND) : t(i18nKeys.OR)} ${toLabel(m.name)}`,
      ),
      labels => labels.join(' '),
    );
  };

  return (
    <Stack>
      <Group align="start">
        <Stack gap={4}>
          <Text>{t(i18nKeys.SENDING_MEDIUM_MANAGER.DEFAULT_USAGE_DESCRIPTION)}</Text>
          <Text fw="bold">{formatCompanyDefault(company.sending_media_attributes)}</Text>
        </Stack>
      </Group>

      <SendingMediumSelector
        mediums={form.values.sendingMedium || []}
        onCreateFirst={createFirstMedium}
        onInsert={medium => form.insertListItem('sendingMedium', medium)}
        onRemove={index => form.removeListItem('sendingMedium', index)}
        onChangeName={(index, name) => form.setFieldValue(`sendingMedium.${index}.name`, name)}
        onChangeForceSending={(index, forceSending) =>
          form.setFieldValue(`sendingMedium.${index}.force_sending`, forceSending)
        }
      />
    </Stack>
  );
};
